/**
 * AdminHelpFeedbackPage — admin-only triage table for help-page feedback.
 *
 * Goal: surface the *un-helpful notes* (with page slug + timestamp)
 * so the team can see *why* people didn't find a help page useful —
 * far more actionable than aggregate counts.
 *
 * Filters:
 *   - "only with notes" (default ON) — collapses noise from
 *     thumbs-down without an explanation
 *   - "only thumbs-down" — narrows to the rows that signal "fix this"
 *   - page slug — for when we ship more help pages later
 *
 * Auth: admin-gated. Non-admins are redirected to /. Backend also
 * enforces, of course — this is purely a UX gate.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import {
  ShieldCheck,
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_PAGES = [
  { value: '', label: 'All pages' },
  { value: 'email-providers', label: 'email-providers' },
];

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return iso;
  }
};

export const AdminHelpFeedbackPage = () => {
  const { user, loading } = useAuth();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pageFilter, setPageFilter] = useState('');
  const [onlyWithNotes, setOnlyWithNotes] = useState(true);
  const [onlyUnhappy, setOnlyUnhappy] = useState(false);

  const fetchRows = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 200 };
      if (pageFilter) params.page = pageFilter;
      if (onlyWithNotes) params.only_with_notes = true;
      if (onlyUnhappy) params.only_unhappy = true;
      const res = await axios.get(`${API}/admin/help-feedback`, { params });
      setRows(res.data?.rows || []);
      setCount(res.data?.count || 0);
    } catch {
      setRows([]);
      setCount(0);
    } finally {
      setBusy(false);
    }
  }, [pageFilter, onlyWithNotes, onlyUnhappy]);

  useEffect(() => {
    if (user?.is_admin) fetchRows();
  }, [user?.is_admin, fetchRows]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  return (
    <div
      className="min-h-screen bg-[#05050A] text-white antialiased"
      data-testid="admin-help-feedback-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.75} />
            <span className="text-sm font-semibold tracking-tight">
              Government Shield
            </span>
            <span className="ml-2 text-[10px] uppercase tracking-[2px] text-primary/80 font-mono">
              admin
            </span>
          </Link>
          <Link
            to="/app"
            className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-12 sm:py-16">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-3">
            admin · help feedback
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            Why people don't find our help pages helpful.
          </h1>
          <p className="mt-3 text-sm text-zinc-400 max-w-2xl">
            Raw feedback rows from <code>/help/*</code> pages, most
            recent first. The notes column is where the actionable
            signal lives — read these and decide what to rewrite.
          </p>
        </div>

        {/* Filters */}
        <div
          className="rounded-md border border-white/10 bg-[#0A0A0F] p-4 mb-6 flex flex-wrap items-center gap-4"
          data-testid="admin-help-feedback-filters"
        >
          <label className="flex items-center gap-2 text-sm">
            <span className="text-slate-400">Page</span>
            <select
              value={pageFilter}
              onChange={(e) => setPageFilter(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-slate-200 text-sm"
              data-testid="admin-help-feedback-page-select"
            >
              {ALLOWED_PAGES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={onlyWithNotes}
              onChange={(e) => setOnlyWithNotes(e.target.checked)}
              data-testid="admin-help-feedback-notes-toggle"
            />
            <span className="text-slate-300">Only with notes</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={onlyUnhappy}
              onChange={(e) => setOnlyUnhappy(e.target.checked)}
              data-testid="admin-help-feedback-unhappy-toggle"
            />
            <span className="text-slate-300">Only thumbs-down</span>
          </label>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchRows}
            disabled={busy}
            className="ml-auto text-xs gap-1.5"
            data-testid="admin-help-feedback-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <span className="text-xs text-slate-500 font-mono" data-testid="admin-help-feedback-count">
            {count} row{count === 1 ? '' : 's'}
          </span>
        </div>

        {/* Table */}
        <div
          className="rounded-md border border-white/10 bg-[#0A0A0F] overflow-hidden"
          data-testid="admin-help-feedback-table-wrap"
        >
          {rows.length === 0 ? (
            <div
              className="p-8 text-center text-sm text-slate-500"
              data-testid="admin-help-feedback-empty"
            >
              {busy ? 'Loading…' : 'No feedback matches the current filters.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[2px] text-slate-500">
                <tr>
                  <th className="text-left p-3 font-semibold">When</th>
                  <th className="text-left p-3 font-semibold">Page</th>
                  <th className="text-left p-3 font-semibold">Vote</th>
                  <th className="text-left p-3 font-semibold w-2/5">Note</th>
                  <th className="text-left p-3 font-semibold">Client</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-white/[0.02] transition-colors"
                    data-testid={`admin-help-feedback-row-${r.id}`}
                  >
                    <td className="p-3 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                      {formatTs(r.updated_at || r.created_at)}
                    </td>
                    <td className="p-3 text-slate-300 font-mono text-[11px]">
                      {r.page}
                    </td>
                    <td className="p-3">
                      {r.helpful ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs">
                          <ThumbsUp className="w-3.5 h-3.5" /> helpful
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-red-400 text-xs">
                          <ThumbsDown className="w-3.5 h-3.5" /> not really
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-slate-300 leading-relaxed">
                      {r.note ? (
                        <span data-testid={`admin-help-feedback-note-${r.id}`}>
                          {r.note}
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-500 font-mono text-[11px]">
                      {r.fp_short}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminHelpFeedbackPage;
